import * as Operators from 'customer-data-filters-ui/filterQueryFormat/operator/Operators';
import * as PropertyTypes from 'customer-data-objects/property/PropertyTypes';
import { RESERVE_WORDS } from '../crmSearch/constants';
import { List, Map as ImmutableMap } from 'immutable';
import curry from 'transmute/curry';
import identity from 'transmute/identity';
import invariant from 'react-utils/invariant';
import isString from 'transmute/isString';
import protocol from 'transmute/protocol';
import { STATIC } from '../../types/operator/RollingDatePointType';
export const ExtensibleReportOperatorTypes = {
  EQUAL: 'EQ',
  GREATER_OR_EQUAL: 'GTE',
  GREATER: 'GT',
  IN_RANGE: 'BETWEEN',
  IN: 'IN',
  LESS_OR_EQUAL: 'LTE',
  LESS: 'LT',
  NOT_EQUAL: 'NEQ',
  NOT_IN: 'NOT_IN'
};
export const TypeToOperator = ImmutableMap({
  [ExtensibleReportOperatorTypes.EQUAL]: Operators.Equal,
  [ExtensibleReportOperatorTypes.GREATER_OR_EQUAL]: Operators.GreaterOrEqual,
  [ExtensibleReportOperatorTypes.GREATER]: Operators.Greater,
  [ExtensibleReportOperatorTypes.IN_RANGE]: Operators.InRange,
  [ExtensibleReportOperatorTypes.IN]: Operators.In,
  [ExtensibleReportOperatorTypes.LESS_OR_EQUAL]: Operators.LessOrEqual,
  [ExtensibleReportOperatorTypes.LESS]: Operators.Less,
  [ExtensibleReportOperatorTypes.NOT_EQUAL]: Operators.NotEqual,
  [ExtensibleReportOperatorTypes.NOT_IN]: Operators.NotIn
});
const defaultConvertTo = curry((type, operator) => {
  const result = {
    operator: type,
    property: operator.field.name
  };
  if (operator.has('highValue')) {
    result.highValue = operator.highValue;
  }
  if (operator.has('value')) {
    result.value = operator.value;
  }
  if (operator.has('propertySuffix')) {
    result.propertySuffix = operator.propertySuffix;
  }
  if (operator.field.type === PropertyTypes.STRING && [Operators.WildCardEqual, Operators.NotWildCardEqual].includes(operator.constructor)) {
    result.value = operator.value.filter(identity).map((str = '') => {
      let safeValue = str.trim();
      if (RESERVE_WORDS.includes(safeValue)) {
        safeValue = `"${safeValue}"`;
      }
      return safeValue;
    }).join(' OR ');
  }
  if (operator.field.type === PropertyTypes.STRING && [Operators.EqualAny, Operators.NotEqualAny].includes(operator.constructor)) {
    result.propertySuffix = 'raw';
    result.value = operator.value.filter(identity).map((str = '') => {
      let safeValue = str.trim().toLowerCase();
      if (RESERVE_WORDS.includes(safeValue)) {
        safeValue = `"${safeValue}"`;
      }
      return safeValue;
    }).join(' OR ');
  }
  if (operator.field.type === PropertyTypes.BOOLEAN && operator.has('value')) {
    result.value = operator.value ? 'true' : 'false';
  }
  if (![Operators.Known, Operators.NotKnown].includes(operator.constructor) && [PropertyTypes.DATE, PropertyTypes.DATE_TIME].includes(operator.field.type)) {
    result.dateTimeFormat = 'DATE';
  }
  return result;
});
const convertToIn = curry((type, operator) => ({
  operator: type,
  property: operator.field.name,
  values: operator.value.toArray()
}));
const convertToNow = curry((type, operator) => ({
  operator: type,
  property: operator.field.name,
  value: '__hs__NOW'
}));
const invalidOperator = operator => {
  invariant(false, 'cannot convert filter operator "%s" to ExtensibleReport filter', operator.name);
};
export const convertToExtensibleReportFilter = protocol({
  name: 'convertToExtensibleReportFilter',
  args: [protocol.TYPE, isString],
  fallback: invalidOperator
});
convertToExtensibleReportFilter.implement(Operators.AssociatedTo, defaultConvertTo(ExtensibleReportOperatorTypes.EQUAL));
convertToExtensibleReportFilter.implement(Operators.Equal, defaultConvertTo(ExtensibleReportOperatorTypes.EQUAL));
convertToExtensibleReportFilter.implement(Operators.GreaterOrEqual, defaultConvertTo(ExtensibleReportOperatorTypes.GREATER_OR_EQUAL));
convertToExtensibleReportFilter.implement(Operators.Greater, defaultConvertTo(ExtensibleReportOperatorTypes.GREATER));
convertToExtensibleReportFilter.implement(Operators.InRange, defaultConvertTo(ExtensibleReportOperatorTypes.IN_RANGE));
convertToExtensibleReportFilter.implement(Operators.NotInRange, defaultConvertTo(ExtensibleReportOperatorTypes));
convertToExtensibleReportFilter.implement(Operators.In, convertToIn(ExtensibleReportOperatorTypes.IN));
convertToExtensibleReportFilter.implement(Operators.Less, defaultConvertTo(ExtensibleReportOperatorTypes.LESS));
convertToExtensibleReportFilter.implement(Operators.LessOrEqual, defaultConvertTo(ExtensibleReportOperatorTypes.LESS_OR_EQUAL));
convertToExtensibleReportFilter.implement(Operators.NotEqual, defaultConvertTo(ExtensibleReportOperatorTypes.NOT_EQUAL));
convertToExtensibleReportFilter.implement(Operators.EqualAny, defaultConvertTo(ExtensibleReportOperatorTypes.EQUAL));
convertToExtensibleReportFilter.implement(Operators.NotEqualAny, defaultConvertTo(ExtensibleReportOperatorTypes.NOT_EQUAL));
convertToExtensibleReportFilter.implement(Operators.Equal, defaultConvertTo(ExtensibleReportOperatorTypes.EQUAL));
convertToExtensibleReportFilter.implement(Operators.BeforeNow, convertToNow(ExtensibleReportOperatorTypes.LESS_OR_EQUAL));
convertToExtensibleReportFilter.implement(Operators.NotIn, convertToIn(ExtensibleReportOperatorTypes.NOT_IN));

/**
 * `null` as a value can cause a whole bunch of issues so
 * lets always convert it over to a string.
 */
function convertNullToString(value) {
  return value === null ? 'null' : value;
}
function convertStringToBool(value) {
  return typeof value === 'string' ? value === 'true' : value;
}
function defaultConvertFrom(Operator, field, entry) {
  const {
    value
  } = entry;
  const safeValue = field.type === PropertyTypes.BOOLEAN ? convertStringToBool(value) : convertNullToString(value);
  if (field.type === PropertyTypes.STRING && [Operators.Equal].includes(Operator)) {
    const safeStringValue = typeof value === 'string' ? value : `${value}`;
    return Operator.of(field, safeStringValue);
  }
  if ([PropertyTypes.DATE, PropertyTypes.DATE_TIME].includes(field.type) && [Operators.LessOrEqual].includes(Operator)) {
    if (value === '__hs__NOW') {
      return Operators.BeforeNow.ofUnsafe(field, safeValue);
    }
  }
  if ([Operators.AfterDateStaticOrDynamic, Operators.BeforeDateStaticOrDynamic].includes(Operator)) {
    return Operator.of(field, value, STATIC);
  }
  return Operator.ofUnsafe(field, safeValue);
}
function convertFromIn(Operator, field, entry) {
  const {
    values
  } = entry;
  const operatorValues = values ? List(values.map(convertNullToString)) : List();
  return Operator.of(field, operatorValues);
}
function convertFromInRange(Operator, field, opts) {
  // Accept legacy field in addition to new one (#343)
  const {
    ['high_value']: highValueLegacy,
    highValue: highValueDefault,
    value
  } = opts;
  const highValue = typeof highValueDefault !== 'undefined' ? highValueDefault : highValueLegacy;
  return Operator.of(field, value, highValue);
}
export const convertFromExtensibleReportsFilters = () => ImmutableMap({
  [ExtensibleReportOperatorTypes.EQUAL]: defaultConvertFrom,
  [ExtensibleReportOperatorTypes.GREATER]: defaultConvertFrom,
  [ExtensibleReportOperatorTypes.GREATER_OR_EQUAL]: defaultConvertFrom,
  [ExtensibleReportOperatorTypes.IN]: convertFromIn,
  [ExtensibleReportOperatorTypes.IN_RANGE]: convertFromInRange,
  [ExtensibleReportOperatorTypes.LESS]: defaultConvertFrom,
  [ExtensibleReportOperatorTypes.LESS_OR_EQUAL]: defaultConvertFrom,
  [ExtensibleReportOperatorTypes.NOT_EQUAL]: defaultConvertFrom,
  [ExtensibleReportOperatorTypes.NOT_IN]: convertFromIn
});